/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

// eslint-disable-next-line no-unused-vars
import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap, cx } from '@atlaskit/css';
import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import CrossIcon from '@atlaskit/icon/core/cross';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';

import { remixReadyMessages } from './messages';

export { remixReadyMessages };

const styles = cssMap({
	container: {
		position: 'fixed',
		bottom: token('space.200'),
		left: '50%',
		transform: 'translateX(-50%)',
		display: 'flex',
		alignItems: 'center',
		minWidth: '360px',
		minHeight: '48px',
		gap: token('space.100'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.100'),
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.overlay'),
		zIndex: 600,
	},
	absolute: {
		position: 'absolute',
	},
	readyMessage: {
		flexGrow: 1,
		flexShrink: 0,
		flexBasis: 'auto',
		paddingLeft: token('space.100'),
		color: token('color.text.subtle'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
	},
});

interface RemixReadyToastProps {
	isUpdate?: boolean;
	showActions?: boolean;
	onView?: () => void;
	onClose?: (options?: { auto?: boolean }) => void;
}

const AUTO_DISMISS_DELAY_MS = 10000;
const SHORT_AUTO_DISMISS_DELAY_MS = 1000;

export function RemixReadyToast({
	isUpdate = false,
	onView,
	onClose,
	showActions = true,
}: RemixReadyToastProps = {}): JSX.Element {
	const { formatMessage } = useIntl();
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [isAbsolute, setIsAbsolute] = useState(false);

	useLayoutEffect(() => {
		if (!wrapperRef.current) {
			return;
		}
		// no scrollParent, such as Jira description editor
		// if in the future, they do have scrollParent, then we need to check if offsetParent is in scrollParent
		const scrollParent = findOverflowScrollParent(wrapperRef.current);
		if (!scrollParent) {
			// wrapperRef.current.offsetParent is null when it's fixed
			// so we have to check the parentElement's offsetParent
			const offsetParent = wrapperRef.current.parentElement?.offsetParent;
			if (offsetParent) {
				const offsetParentRect = offsetParent.getBoundingClientRect();
				if (offsetParentRect.bottom < window.innerHeight) {
					setIsAbsolute(true);
				}
			}
		}
	}, []);

	useEffect(() => {
		const timeoutId = window.setTimeout(
			() => {
				onClose?.({ auto: true });
			},
			showActions ? AUTO_DISMISS_DELAY_MS : SHORT_AUTO_DISMISS_DELAY_MS,
		);

		return () => window.clearTimeout(timeoutId);
	}, [onClose, showActions]);

	// Evaluated before the gate check so the experiment still records an exposure.
	const isCreateKeyword = expValEquals('cc_maui_create_keyword', 'isEnabled', true);
	const readyMessage = isUpdate
		? remixReadyMessages.updateReady
		: fg('cc_maui_updated_remix_tooltip_text')
			? remixReadyMessages.remixReadyUpdated
			: isCreateKeyword
				? remixReadyMessages.contentReady
				: remixReadyMessages.remixReady;

	return (
		<Box ref={wrapperRef} xcss={cx(styles.container, isAbsolute && styles.absolute)}>
			<CheckCircleIcon label="" color={token('color.icon.success')} />
			<Box xcss={styles.readyMessage}>{formatMessage(readyMessage)}</Box>
			{showActions && (
				<>
					<Button appearance="primary" onClick={onView}>
						{formatMessage(remixReadyMessages.viewButton)}
					</Button>
					<IconButton
						spacing="compact"
						appearance="subtle"
						label={formatMessage(remixReadyMessages.closeButton)}
						icon={() => <CrossIcon label="" />}
						onClick={() => onClose?.()}
					/>
				</>
			)}
		</Box>
	);
}
